import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, Info } from 'lucide-react';

import { AuthShell } from './AuthShell';
import { forgotSchema, type ForgotInput } from './schemas';
import { supabase } from '../../lib/supabase';
import { logAudit } from '../../lib/audit';
import { cn } from '../../lib/cn';
import { useToast } from '../../components/Toast';
import styles from './auth.module.css';

const COOLDOWN_S = 30;

export function ForgotScreen() {
  const toast = useToast();
  const [formError, setFormError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => { setCooldown((s) => s - 1); }, 1000);
    return () => { clearTimeout(t); };
  }, [cooldown]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotInput>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async ({ email }) => {
    setFormError(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset`,
    });
    if (error) {
      setFormError('לא הצלחנו לשלוח קישור — נסה שוב');
      return;
    }
    await logAudit({ action: 'auth.password_reset_requested' });
    toast.success('קישור איפוס נשלח');
    setSentTo(email);
    setCooldown(COOLDOWN_S);
  });

  return (
    <AuthShell
      title="שכחת סיסמה?"
      subtitle="הכנס את האימייל שלך · נשלח קישור איפוס מאובטח"
      brandSide={{
        title: 'איפוס מהיר ובטוח',
        body: 'קישור חד-פעמי תקף לשעה אחת בלבד.',
      }}
    >
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <div className={styles.field}>
          <label htmlFor="forgot-email">אימייל</label>
          <input
            id="forgot-email"
            type="email"
            dir="ltr"
            autoComplete="email"
            placeholder="you@company.co.il"
            className={cn(styles.input, errors.email && styles.invalid)}
            {...register('email')}
          />
          {errors.email ? <p className={styles.error}>{errors.email.message}</p> : null}
        </div>

        {sentTo ? (
          <div className={styles.success}>
            שלחנו קישור איפוס ל-<strong className="ltr-text">{sentTo}</strong>.
          </div>
        ) : null}

        {formError ? <p className={styles.formError}>{formError}</p> : null}

        <button
          type="submit"
          className={styles.primary}
          disabled={isSubmitting || cooldown > 0}
        >
          <Send size={14} />
          {cooldown > 0 ? (
            <>
              שלח שוב בעוד <span className="num">{String(cooldown)}</span> שנ׳
            </>
          ) : (
            'שלח קישור'
          )}
        </button>

        <div className={styles.infoBox}>
          <Info size={16} />
          <div>
            <strong>מה לעשות עכשיו?</strong>
            <p>בדוק את האינבוקס תוך 5 דקות. אם לא הגיע — בדוק ספאם או נסה שוב.</p>
          </div>
        </div>

        <Link to="/auth/login" className={styles.back}>
          <ArrowLeft size={12} className="flip-rtl" />
          חזור להתחברות
        </Link>
      </form>
    </AuthShell>
  );
}
